import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface FormCycle {
  id: string
  startsAt: string
  endsAt?: string
}

interface ResourceWithFormCycles {
  form?: {
    schedule?: {
      cycles?: FormCycle[]
    } | null
  }
}

// Structured localStorage for filters and cycle selection
export interface FormFiltersStorage {
  filters: Record<string, unknown>
  cycleId?: string
}

/**
 * Manages form cycle selection with URL/localStorage sync.
 * Supports multiple GraphQL fragments, sorts by start date descending.
 */
export const useFormCycleSelection = (
  resource: ResourceWithFormCycles | null | undefined,
  savedStorage?: FormFiltersStorage
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const sortedCycles = useMemo(() => {
    if (!resource?.form?.schedule?.cycles) return []

    const rawCycles = resource.form.schedule.cycles

    const normalizedCycles: FormCycle[] = rawCycles.map((cycle) => ({
      id: cycle.id,
      startsAt: cycle.startsAt,
      endsAt: cycle.endsAt || undefined,
    }))

    return normalizedCycles.sort(
      (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()
    )
  }, [resource])

  const urlCycleId = searchParams.get('cycleId')
  const savedCycleId = savedStorage?.cycleId
  const defaultCycleId = sortedCycles?.[0]?.id ?? null

  const validUrlCycleId = urlCycleId && sortedCycles.find((cycle) => cycle.id === urlCycleId)?.id
  const validSavedCycleId =
    savedCycleId && sortedCycles.find((cycle) => cycle.id === savedCycleId)?.id

  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(
    validUrlCycleId ?? validSavedCycleId ?? defaultCycleId ?? null
  )

  const handleCycleChange = useCallback(
    (cycleId: string | null) => {
      setSelectedCycleId(cycleId)

      if (cycleId) {
        setSearchParams({ cycleId })
      } else {
        setSearchParams({})
      }
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (sortedCycles.length > 0) {
      if (validUrlCycleId && selectedCycleId !== validUrlCycleId) {
        setSelectedCycleId(validUrlCycleId)
      } else if (validSavedCycleId && !validUrlCycleId && selectedCycleId !== validSavedCycleId) {
        setSelectedCycleId(validSavedCycleId)
        const newParams = new URLSearchParams(searchParams)
        newParams.set('cycleId', validSavedCycleId)
        setSearchParams(newParams)
      } else if (!validUrlCycleId && !validSavedCycleId && !selectedCycleId && defaultCycleId) {
        setSelectedCycleId(defaultCycleId)
        const newParams = new URLSearchParams(searchParams)
        newParams.set('cycleId', defaultCycleId)
        setSearchParams(newParams)
      }
    }
  }, [
    sortedCycles.length,
    validUrlCycleId,
    validSavedCycleId,
    selectedCycleId,
    defaultCycleId,
    searchParams,
    setSearchParams,
  ])

  return {
    cycles: sortedCycles,
    selectedCycleId,
    handleCycleChange,
    hasCycles: sortedCycles.length > 1,
  }
}
